import React, { useState } from "react";
import "./MicroPendingTable.css";
import { toast } from "react-toastify";
import { Table } from "antd";
import { uploadPetrifilmSingleCount, deletePetrifilmMapping } from "../../../../actions/micro";
import MicroPendingTableRow from "./MicroPendingTableRow";
import MicroPendingTableCell from "./MicroPendingTableCell";

function MicroPendingTable({
  samples,
  setSamples,
  isPending,
  hideCountAndAction,
  tableLoading,
  selectedTat,
  handleFetchPending,
  user,
}) {
  const [submittingKey, setSubmittingKey] = useState(-1);
  const [removingKey, setRemovingKey] = useState(-1);
  /**
   * Check if the row should be disabled
   * @param {Object} record sample object {key, wo_id, genista_id, df, petrifilm, status, mapping, recheck, counts, edit, submitting}
   * @returns {Boolean} true if should disable
   */
  const checkRowDisabled = (record) => !record.recheck && !record.edit && record.status !== "Pending" && record.status !== "Retest";

  /**
   * Check if the inputs and submit button should be disabled.
   * @returns {Boolean} true if should disable
   */
  const checkDisableInteraction = () => submittingKey > -1 || removingKey > 1;

  /**
   * Update sample in samples list
   * @param {Number} index index of sample in samples array
   * @param {Object} sample updated sample
   */
  const updateSample = (index, sample) => {
    const samplesCopy = [...samples];
    samplesCopy[index] = sample;
    setSamples(samplesCopy);
    // if (samplesCopy.every((_sample) => checkDisabled(_sample))) { // autoclose modal if all samples are ready
    //   handleReset();
    // }
  };

  /**
   * Handle submit count (including recheck).
   * @param {Object} sample sample obj
   * @param {Object} inputValues {count, df}
   * @returns {void}
   */
  const handleSubmit = async (sample, inputValues) => {
    const index = sample.key;
    setSubmittingKey(index);
    const rawCount = inputValues?.counts;
    if (Number.isNaN(rawCount)) {
      setSubmittingKey(-1);
      toast.error("Raw count must be a number");
      return;
    }

    const {
      wo_id,
      genista_id,
      analysis,
      petrifilm,
      df,
      recheck,
      mapping,
      wo,
      sample_id,
      test_in_sample_id,
    } = sample;

    let inputtedDf;
    if (isPending && !df) {
      inputtedDf = inputValues?.df;
      if (Number.isNaN(inputtedDf)) {
        setSubmittingKey(-1);
        toast.error("DF must be a number");
        return;
      }
    }

    const payload = {
      wo_id,
      genista_id,
      analysis,
      petrifilm,
      df: inputtedDf ? String(inputtedDf) : df,
      counts: String(rawCount),
      recheck,
      mapping,
      wo,
      sample_id,
      test_in_sample_id,
    };

    const { success, message, result } = await uploadPetrifilmSingleCount(payload);

    if (success) {
      updateSample(index, {
        key: sample.key, ...result, edit: false,
      });
    } else {
      updateSample(index, { ...sample });
      toast.error(message);
    }
    setSubmittingKey(-1);
  };

  /**
   * Handle removing the analysis-petrifilm mapping
   * @param {*} sample sample obj
   */
  const handleRemoveMapping = async (sample) => {
    const index = sample.key;
    setRemovingKey(index);
    const payload = { ...sample };
    delete payload.key;
    const { success, message } = await deletePetrifilmMapping(payload);
    if (success) {
      await handleFetchPending(selectedTat);
    } else {
      toast.error(message);
    }
    setRemovingKey(-1);
  };

  const tableColumns = [
    {
      title: "WO",
      dataIndex: "wo_id",
      key: "wo_id",
      permission: "no-permission",
    },
    {
      title: "Genista ID",
      dataIndex: "genista_id",
      key: "genista_id",
      permission: "no-permission",
    },
    {
      title: "Analysis",
      dataIndex: "analysis",
      key: "analysis",
      permission: "no-permission",
    },
    {
      title: "Petrifilm",
      dataIndex: "petrifilm",
      key: "petrifilm",
      permission: "no-permission",
    },
    {
      title: "DF",
      dataIndex: "df",
      key: "df",
      permission: "no-permission",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 120,
      align: "center",
      permission: "view_sample_qc_history",
    },
    // {
    //   title: "Mapping",
    //   dataIndex: "mapping",
    //   key: "mapping",
    //   align: "center",
    // },
  ];

  return (
    <Table
      components={{
        body: {
          row: MicroPendingTableRow,
          cell: MicroPendingTableCell,
        },
      }}
      onRow={(record, index) => (
        { record, index, checkRowDisabled }
      )}
      expandable={{
        showExpandColumn: false,
        rowExpandable: (record) => record.recheck,
        defaultExpandAllRows: true,
        expandedRowRender: () => <span className="MicroPendingTable__ExpandedCell">Verify raw count for the above row</span>,
      }}
      sticky
      tableLayout="fixed"
      columns={tableColumns.map((col) => ({
        ...col,
        onCell: (record) => ({
          record,
          dataIndex: col.dataIndex,
          title: col.title,
          user,
          handleSubmit,
          handleRemoveMapping,
          updateSample,
          checkRowDisabled,
          checkDisableInteraction,
          submittingKey,
          removingKey,
          isPending,
        }),
      })).filter((col) => user.permissionMap[col.permission])}
      dataSource={samples}
      pagination={false}
      bordered
      className={`MicroPendingTable ${hideCountAndAction ? "MicroPendingTable__StickyChange" : ""}`}
      loading={tableLoading}
    />
  );
}

export default MicroPendingTable;
